import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Handshake, TrendingUp, Users, Award } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BecomePartnerPage = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    profession: '',
    city: '',
    state: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/partner-application`, formData);
      
      if (response.status === 200) {
        toast({
          title: "Partnership Request Submitted! 🤝",
          description: "Thank you! We've received your application and will contact you within 24 hours.",
        });

        setFormData({
          name: '',
          mobile: '',
          profession: '',
          city: '',
          state: ''
        });
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error submitting partner application:', error);
      }
      toast({
        title: "Submission Error",
        description: "There was an error submitting your application. Please try again or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <WhatsAppButton />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-emerald-50 via-white to-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-4">
              <div className="w-20 h-20 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto">
                <Handshake className="w-10 h-10 text-emerald-600" />
              </div>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Become a <span className="text-emerald-600">Partner</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Join LOANYARD CAPITAL's partner network and grow your business with attractive commissions and dedicated support
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
              Why Partner With Us?
            </h2>
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <div className="text-center p-6 bg-slate-50 rounded-xl">
                <div className="w-16 h-16 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">High Commissions</h3>
                <p className="text-slate-600">Earn attractive commissions on every successful loan disbursement</p>
              </div>
              <div className="text-center p-6 bg-slate-50 rounded-xl">
                <div className="w-16 h-16 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Dedicated Support</h3>
                <p className="text-slate-600">Get personalized assistance from our dedicated relationship manager</p>
              </div>
              <div className="text-center p-6 bg-slate-50 rounded-xl">
                <div className="w-16 h-16 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Wide Product Range</h3>
                <p className="text-slate-600">Access to multiple financing solutions ensures best deals for your clients</p>
              </div>
            </div>

            {/* Partner Types */}
            <div className="bg-emerald-50 p-8 rounded-2xl border border-emerald-200 mb-16">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Who Can Partner?</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-slate-900">DSA (Direct Selling Agents)</h4>
                    <p className="text-slate-600 text-sm">Individual agents looking to earn commission</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Real Estate Developers</h4>
                    <p className="text-slate-600 text-sm">Builders and developers offering home loans</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Channel Partners</h4>
                    <p className="text-slate-600 text-sm">Organizations with existing client base</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Financial Advisors</h4>
                    <p className="text-slate-600 text-sm">CAs, financial planners, and consultants</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 bg-gradient-to-br from-slate-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl border-2 border-slate-200 p-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-2">Partner Application Form</h2>
              <p className="text-slate-600 mb-8">Fill out the form below and we'll get back to you within 24 hours</p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mobile">Mobile No. *</Label>
                  <Input
                    id="mobile"
                    type="tel"
                    placeholder="+91 XXXXX XXXXX"
                    value={formData.mobile}
                    onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="profession">Profession *</Label>
                  <Select value={formData.profession} onValueChange={(value) => setFormData({...formData, profession: value})} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your profession" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="chartered-accountant">Chartered Accountant</SelectItem>
                      <SelectItem value="dsa">DSA</SelectItem>
                      <SelectItem value="housewife">Housewife</SelectItem>
                      <SelectItem value="freelancer">Freelancer</SelectItem>
                      <SelectItem value="real-estate-advisor">Real Estate Advisor</SelectItem>
                      <SelectItem value="others">Others</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      placeholder="Enter your city"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      placeholder="Enter your state"
                      value={formData.state}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" 
                  size="lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Partnership Request'}
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  By submitting this form, you agree to our partnership terms and conditions
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BecomePartnerPage;
